import React, { useCallback, useMemo, useState } from "react"
import { Box } from "@mui/material"
import { useAuth } from "@/features/auth/context/AuthContext"
import { useProducts } from "@/shared/hooks/useProducts"
import { useImageGalleryViewer } from "@/shared/hooks/useImageGalleryViewer"
import ImageGallery from "@/shared/components/image-gallery"
import { useSnackbar } from "@/shared/context/SnackbarContext"
import { DEFAULT_PRODUCT_VIDEO_PROMPT, generateProductVideo } from "@/shared/services/video"
import { useGalleryImages, GalleryImage } from "@/features/gallery/hooks/useGalleryImages"
import { useGallerySelection } from "@/features/gallery/hooks/useGallerySelection"
import { useZipDownload } from "@/features/gallery/hooks/useZipDownload"
import GalleryToolbar from "@/features/gallery/components/GalleryToolbar"
import GalleryImageGrid from "@/features/gallery/components/GalleryImageGrid"
import { EmptyGallery, LoadingGallery } from "@/features/gallery/components/EmptyGallery"

const GalleryPage: React.FC = () => {
  const { user } = useAuth()
  const { showSnackbar } = useSnackbar()
  const { products, loading } = useProducts(user?.uid ?? null)
  const { allImages } = useGalleryImages(products)
  const [generatedVideos, setGeneratedVideos] = useState<GalleryImage[]>([])
  const [generatingVideo, setGeneratingVideo] = useState(false)

  const galleryItems = useMemo(() => {
    const merged = [...generatedVideos, ...allImages]
    return merged.sort((a, b) => {
      if (!a.createdAt && !b.createdAt) return 0
      if (!a.createdAt) return 1
      if (!b.createdAt) return -1
      return b.createdAt.getTime() - a.createdAt.getTime()
    })
  }, [allImages, generatedVideos])

  const groupedItems = useMemo(() => {
    const map = new Map<string, GalleryImage[]>()
    galleryItems.forEach(item => {
      const dateLabel = item.createdAt
        ? item.createdAt.toLocaleDateString("en-US", {
            weekday: "short",
            day: "numeric",
            month: "short",
            year: "numeric"
          })
        : "Unknown Date"
      const existing = map.get(dateLabel) ?? []
      existing.push(item)
      map.set(dateLabel, existing)
    })

    return [...map.entries()].map(([dateLabel, images]) => ({ dateLabel, images }))
  }, [galleryItems])

  const {
    isSelected,
    toggle,
    toggleDate,
    isDateAllSelected,
    isDateSomeSelected,
    selectAll,
    clearAll,
    selectedCount,
    selectedImages
  } = useGallerySelection(galleryItems)
  const { downloading, downloadAsZip } = useZipDownload()
  const { viewer, openWithItems, closeViewer } = useImageGalleryViewer()

  const selectedImageItems = useMemo(
    () => selectedImages.filter(item => (item.mediaType ?? "image") === "image"),
    [selectedImages]
  )

  const canGenerateVideo =
    selectedImageItems.length > 0 && selectedImageItems.length <= 5 && selectedImageItems.length === selectedCount

  const handleOpen = useCallback(
    (img: GalleryImage, groupImages: GalleryImage[]) => {
      if ((img.mediaType ?? "image") === "video") {
        return
      }

      const imageItems = groupImages.filter(i => (i.mediaType ?? "image") === "image")
      const items = imageItems.map(i => ({ url: i.url, alt: i.productName }))
      const index = imageItems.findIndex(
        i => i.productId === img.productId && i.generatedImageIndex === img.generatedImageIndex
      )
      openWithItems(items, index >= 0 ? index : 0)
    },
    [openWithItems]
  )

  const handleDownloadZip = useCallback(() => {
    void downloadAsZip(selectedImages)
  }, [downloadAsZip, selectedImages])

  const handleGenerateVideo = useCallback(async () => {
    if (!canGenerateVideo || generatingVideo) {
      return
    }

    try {
      setGeneratingVideo(true)
      const response = await generateProductVideo({
        prompt: DEFAULT_PRODUCT_VIDEO_PROMPT,
        imageUrls: selectedImageItems.map(item => item.url),
        aspectRatio: "16:9"
      })

      const now = new Date()
      const generatedVideoItem: GalleryImage = {
        assetId: `video:${now.getTime()}:${Math.random().toString(36).slice(2, 8)}`,
        url: response.videoUrl,
        productId: "generated-video",
        productName: "Generated Video",
        generatedImageIndex: -1,
        mediaType: "video",
        createdAt: now
      }

      setGeneratedVideos(prev => [generatedVideoItem, ...prev])
      clearAll()
      showSnackbar("Video generated successfully.", "success")
    } catch (error) {
      console.error("Failed to generate video:", error)
      showSnackbar("Failed to generate video. Please try again.", "error")
    } finally {
      setGeneratingVideo(false)
    }
  }, [canGenerateVideo, clearAll, generatingVideo, selectedImageItems, showSnackbar])

  if (loading) return <LoadingGallery />
  if (galleryItems.length === 0) return <EmptyGallery />

  return (
    <Box sx={{ pb: 10 }}>
      <GalleryToolbar
        totalCount={galleryItems.length}
        selectedCount={selectedCount}
        downloading={downloading}
        generatingVideo={generatingVideo}
        canGenerateVideo={canGenerateVideo}
        onSelectAll={selectAll}
        onClearAll={clearAll}
        onDownloadZip={handleDownloadZip}
        onGenerateVideo={handleGenerateVideo}
      />
      <GalleryImageGrid
        groups={groupedItems}
        isSelected={isSelected}
        isDateAllSelected={isDateAllSelected}
        isDateSomeSelected={isDateSomeSelected}
        onSelect={toggle}
        onToggleDate={toggleDate}
        onOpen={handleOpen}
      />
      {viewer && <ImageGallery items={viewer.items} initialIndex={viewer.initialIndex} onClose={closeViewer} />}
    </Box>
  )
}

export default GalleryPage
